// Líneas de la venta que originó el crédito. El crédito no expone su venta: se busca entre las
// ventas del cliente la que trae este `creditId`.
import { useQuery } from '@tanstack/react-query';
import type { SaleLine } from '@/api/services/retail-wizard/contract';
import { backofficeSalesApi } from '@/api/services/retail-wizard/retail-wizard.client';
import { classifyFailure } from '../failures';
import type { CreditFileBlock } from './useCreditFile';

/** Tope de páginas: un cliente tiene pocas ventas y no vale recorrer de más. */
const MAX_PAGES = 5;

/** Las líneas de la venta del crédito; `null` si no se encontró o es anterior a las líneas. */
export async function fetchCreditSaleLines(
    creditId: string,
    retailCustomerId: string,
): Promise<SaleLine[] | null> {
    let nextToken: string | undefined;
    for (let page = 0; page < MAX_PAGES; page++) {
        const result = await backofficeSalesApi.listByCustomer(retailCustomerId, nextToken);
        const sale = result.items.find((s) => s.creditId === creditId);
        if (sale) return sale.lines && sale.lines.length > 0 ? sale.lines : null;
        if (result.nextToken == null) return null;
        nextToken = result.nextToken;
    }
    return null;
}

export function useCreditSaleLines(
    creditId: string,
    retailCustomerId: string | null | undefined,
): CreditFileBlock<SaleLine[] | null> {
    const query = useQuery({
        queryKey: ['credit-file', 'sale-lines', creditId, retailCustomerId],
        queryFn: () => fetchCreditSaleLines(creditId, retailCustomerId as string),
        enabled: !!retailCustomerId,
        staleTime: 30_000,
        retry: false,
    });
    return {
        data: retailCustomerId ? query.data : null,
        isLoading: query.isLoading,
        failure: classifyFailure(query.error),
        retry: () => {
            void query.refetch();
        },
    };
}
